/**
 * «کیف پول‌های واریز» (owner, 2026-10-16, OD-69): one row per network — paste your own wallet address
 * and save. Network names, explorers and checks are prepared on the server; USDT rows also take the
 * withdraw fee and minimum. Saving needs the admin step-up (asked in place) and is audited.
 */
import { AppContainer, Badge, Button, Card, PageHeader, TextField } from '@hopex/ui';
import { Wallet } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api.ts';
import { CommandDialog } from '../lib/command.tsx';
import { Remote, useRemote } from '../lib/remote.tsx';

interface Row {
  key: string;
  asset: string;
  network: string;
  withdraw: boolean;
  address: string | null;
  active: boolean;
  withdrawFee: string | null;
  withdrawMin: string | null;
}

export function WalletsPage() {
  const r = useRemote<{ wallets: Row[] }>('/wallets');
  const [edit, setEdit] = useState<Row | null>(null);
  return (
    <AppContainer size="content">
      <PageHeader
        title="کیف پول‌های واریز"
        description="آدرس کیف پول خودتان را برای هر شبکه وارد کنید. تتر واریز و برداشت دارد؛ بقیه فقط واریز (برای برداشت، کاربر اول به تتر تبدیل می‌کند)."
      />
      <Remote r={r}>
        {(d) => (
          <div className="flex flex-col gap-2.5" data-wallets>
            {d.wallets.map((w) => (
              <Card
                key={w.key}
                className="flex flex-wrap items-center gap-3 p-4"
                data-wallet={w.key}
              >
                <span className="grid size-10 place-items-center rounded-full bg-accent-tint text-accent-text">
                  <Wallet aria-hidden className="size-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="m-0 flex flex-wrap items-center gap-2 text-[14px] font-bold">
                    {w.asset} · {w.network}
                    <Badge tone={w.active ? 'positive' : 'neutral'}>
                      {w.active ? 'فعال' : 'ثبت نشده'}
                    </Badge>
                    <Badge tone="neutral">{w.withdraw ? 'واریز و برداشت' : 'فقط واریز'}</Badge>
                  </p>
                  <p className="m-0 mt-1 truncate text-[12.5px] text-fg-muted">
                    {w.address ? (
                      <bdi dir="ltr" className="font-mono">
                        {w.address}
                      </bdi>
                    ) : (
                      'هنوز آدرسی ثبت نشده'
                    )}
                    {w.withdraw && w.withdrawFee !== null && (
                      <>
                        {' '}
                        · کارمزد برداشت <bdi dir="ltr">{w.withdrawFee}</bdi> · حداقل{' '}
                        <bdi dir="ltr">{w.withdrawMin}</bdi>
                      </>
                    )}
                  </p>
                </div>
                <Button variant={w.address ? 'secondary' : 'primary'} onPress={() => setEdit(w)}>
                  {w.address ? 'تغییر' : 'ثبت آدرس'}
                </Button>
              </Card>
            ))}
          </div>
        )}
      </Remote>
      {edit && (
        <EditWallet
          w={edit}
          onClose={() => setEdit(null)}
          onDone={() => {
            setEdit(null);
            r.reload();
          }}
        />
      )}
    </AppContainer>
  );
}

function EditWallet({ w, onClose, onDone }: { w: Row; onClose: () => void; onDone: () => void }) {
  const [address, setAddress] = useState(w.address ?? '');
  const [fee, setFee] = useState(w.withdrawFee ?? '');
  const [min, setMin] = useState(w.withdrawMin ?? '');
  return (
    <CommandDialog
      title={`کیف پول ${w.asset} · ${w.network}`}
      confirmLabel="ذخیره"
      reasonRequired={false}
      canSubmit={address.trim().length >= 20 && (!w.withdraw || (!!fee.trim() && !!min.trim()))}
      run={async () => {
        await api.post('/wallets', {
          key: w.key,
          address: address.trim(),
          ...(w.withdraw ? { withdrawFee: fee.trim(), withdrawMin: min.trim() } : {}),
        });
      }}
      onClose={onClose}
      onDone={onDone}
      data-wallet-edit={w.key}
    >
      <TextField label="آدرس کیف پول" value={address} onChange={setAddress} ltr />
      {w.withdraw && (
        <div className="grid grid-cols-2 gap-3">
          <TextField
            label="کارمزد برداشت (تتر)"
            value={fee}
            onChange={setFee}
            ltr
            inputMode="decimal"
          />
          <TextField
            label="حداقل برداشت (تتر)"
            value={min}
            onChange={setMin}
            ltr
            inputMode="decimal"
          />
        </div>
      )}
      <p className="m-0 text-[12px] leading-6 text-fg-muted">
        آدرس قبلی (اگر بود) کنار گذاشته می‌شود و از این به بعد آدرس تازه به کاربران نشان داده
        می‌شود.
      </p>
    </CommandDialog>
  );
}
